/**
 * Grand Quack Auto — math helpers.
 *
 * Keep this file tiny. Only generic, reusable math belongs here.
 * Three.js has its own Vector3/Quaternion helpers — prefer those when working with Three types.
 */

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

/**
 * Frame-rate-independent damping — see Freya Holmer's "Exponential Smoothing" talk.
 * Use instead of lerp when the lerp factor must be independent of frame rate.
 *
 *   x = damp(x, target, smoothing, dt)
 *
 * where smoothing ∈ (0,1). Smaller = snappier. ~0.001 feels like a tight chase cam.
 */
export const damp = (current: number, target: number, smoothing: number, dt: number): number =>
  lerp(target, current, Math.pow(smoothing, dt));

/**
 * Frame-rate-independent angle damping with shortest-path wrapping.
 * Handles the -π/π boundary correctly (won't swing the long way around).
 */
export const dampAngle = (current: number, target: number, smoothing: number, dt: number): number => {
  let diff = target - current;
  if (diff > Math.PI) diff -= Math.PI * 2;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return current + diff * (1 - Math.pow(smoothing, dt));
};

/** Map a value from one range to another, clamped. */
export const remap = (v: number, inMin: number, inMax: number, outMin: number, outMax: number): number => {
  const t = clamp((v - inMin) / (inMax - inMin), 0, 1);
  return outMin + (outMax - outMin) * t;
};

/** Seeded pseudo-random (mulberry32) — use for procedural generation that must be stable across clients. */
export const mulberry32 = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
